import * as THREE from 'three';
import { ROBOT_LAYER } from '../sim/world';

/** Разрешение одного видеоокна на стороне «робота» (до деградации канала) */
export const VIEW_W = 640;
export const VIEW_H = 360;

/**
 * Рендер видов с камер робота. Оба вида рисуются в один скрытый канвас
 * (левая половина — первая камера, правая — вторая), откуда их забирает
 * видеоканал. Между рендером и глазами оператора всегда стоит модель связи.
 */
export class CameraFeeds {
  readonly renderer: THREE.WebGLRenderer;
  readonly canvas: HTMLCanvasElement;

  constructor(camCount: number) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = VIEW_W * camCount;
    this.canvas.height = VIEW_H;
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(VIEW_W * camCount, VIEW_H, false);
    this.renderer.shadowMap.enabled = true;
    this.renderer.setScissorTest(true);
  }

  /** Регион канваса, где нарисован вид i-й камеры: [x, y, w, h] */
  region(i: number): [number, number, number, number] {
    return [i * VIEW_W, 0, VIEW_W, VIEW_H];
  }

  render(scene: THREE.Scene, cameras: THREE.PerspectiveCamera[]): void {
    cameras.forEach((cam, i) => {
      cam.aspect = VIEW_W / VIEW_H;
      cam.updateProjectionMatrix();
      this.renderer.setViewport(i * VIEW_W, 0, VIEW_W, VIEW_H);
      this.renderer.setScissor(i * VIEW_W, 0, VIEW_W, VIEW_H);
      this.renderer.render(scene, cam);
    });
  }
}

/**
 * Вид инструктора/разработчика: третье лицо, полная информация.
 * Отдельный рендерер — оператор его не видит и деградации канала он не подчиняется
 * (п. 6 ТЗ: наборы сигналов оператора и инструктора различаются).
 */
export class InstructorView {
  readonly camera = new THREE.PerspectiveCamera(55, 16 / 9, 0.1, 500);
  private renderer: THREE.WebGLRenderer | null = null;
  private target = new THREE.Vector3();

  constructor() {
    // Инструктор, в отличие от камер робота, видит и сам корпус
    this.camera.layers.enable(ROBOT_LAYER);
  }

  attach(canvas: HTMLCanvasElement): void {
    if (this.renderer) return;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    this.renderer.shadowMap.enabled = true;
  }

  render(scene: THREE.Scene, follow: THREE.Object3D, w: number, h: number): void {
    if (!this.renderer) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();

    const back = new THREE.Vector3(0, 0, -1).applyQuaternion(follow.quaternion).multiplyScalar(5.5);
    const want = follow.position.clone().add(back).add(new THREE.Vector3(0, 3.2, 0));
    this.camera.position.lerp(want, 0.12);
    this.target.lerp(follow.position, 0.2);
    this.camera.lookAt(this.target);

    this.renderer.render(scene, this.camera);
  }
}
